import { INDEXSYMBOL } from "@app/_helpers/constants";
import { AlgoPermissions } from "../../../../../../utProto/webEnums_pb";
import { AuthService } from "@app/modules/auth/provider/auth/auth.service";
import { LoginSource } from "../../profile/components/personal-details/interfaces/personal-details.enum";
import { PlanSubscription } from "../../profile/components/plans/plans.enums";
import { ExchangeId } from "../../../../../../utProto/enums_pb";
import { EXCHANGE_STRINGS } from "@app/_shared/modules/portfolio-form/portfolio-form.enums";

export class UserDetailsResponse {
    success: boolean;
    message: string;
    data: {
        client: Partial<UserDetails>;
        client_id: string;
        is_existing: boolean;
        created_at: string;
    };
}

export class UserDetails {
    address: string | null;
    brokers: string[];
    city: string;
    client_id: string;
    country: string;
    created_at: string;
    dob: string | number | null;
    email: string;
    extra_credit: number;
    full_name: string;
    is_active: boolean;
    login_source: LoginSource;
    login_type: string;
    pan: string;
    phone: string;
    plan: string;
    plan_id: number;
    next_plan_id: number | null;
    plan_start_time: string;
    role: string;
    state: string;
    image: string;
    image_link?: string;
    algo_permissions: number[];
    ucc: string | null;
    pin_code: string | null;
    locality: string | null;
    gst_number: string | null;
    previous_plan: string | null;
    previous_plan_id: number | null;
    previous_pricing_id?: number | null;
    next_plan_id_value?: number | null;

    account_state?: string;
    insufficient_balance?: boolean;
    is_algo_provider?: boolean;
    max_multiplier?: number;
    max_active_algos?: number;

    specialSymbolsPermissionMap: Map<string, boolean>;
    displayName: string;

    constructor(apiResponse: Object) {
        Object.keys(apiResponse).forEach((k) => {
            this[k] = apiResponse[k];
        });

        this.algo_permissions = this.algo_permissions || [];
        this.is_active = this.is_active ?? (this.account_state === 'A');

        this.specialSymbolsPermissionMap = new Map<string, boolean>([
            [INDEXSYMBOL.NIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_NIFTY)],
            [INDEXSYMBOL.BANKNIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_BANKNIFTY)],
            [INDEXSYMBOL.FINNIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_FINNIFTY)],
            [INDEXSYMBOL.MIDCPNIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_MIDCAPNIFTY)],
            [INDEXSYMBOL.SENSEX, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_SENSEX)],
            [INDEXSYMBOL.BANKEX, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_BANKEX)]
        ])

        // If this user has linked SI account, we will prioritize UCC (from SI account) to uTrade Algos client ID
        // for display purpose on header, profile and dashboard section.
        if (this?.ucc)
            this.displayName = this.ucc;
        else
            this.displayName = this.client_id;
    }

    isExternalStrategyAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_EXTERNALSTRATEGY);
    }

    isCreationAllowed(authService: AuthService) {
        return (authService.accessTokenObj.loginSource !== LoginSource.SWASTIKA) || (authService.accessTokenObj.loginSource === LoginSource.SWASTIKA) && this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_HIDECUSTOMPORTFOLIO)
    }

    isDownloadAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_DOWNLOADUPLOADPORTFOLIO)
    }

    isMaginCalculatorAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_MARGINCALCULATOR);
    }

    isPayoffCurveAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_PAYOFFCURVE);
    }

    isOneAiAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_AIASSISTANT_ONEAI);
    }

    isAiRecommendOriginalsAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_AIRECOMMEND_ORIGINALS);
    }

    isPublicPortFolioAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_PUBLICPORTFOLIO);
    }

    isSymbolAllowedInPlan(symbol: string, exchange: string = ExchangeId.EXCHANGEID_NSEFO.toString()) {

        if (this.specialSymbolsPermissionMap.has(symbol)) {
            return this.specialSymbolsPermissionMap.get(symbol);
        }
        if (exchange === ExchangeId.EXCHANGEID_NSEFO.toString()) {
            return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLNSESYMBOLS);
        } else {
            return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLBSESYMBOLS);
        }
    }

    getSelectedSymbols() {
        const isNSE = this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLNSESYMBOLS);
        const isBSE = this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLBSESYMBOLS);
        if (isNSE && isBSE) {
            return null;
        } else if (isNSE) {
            return EXCHANGE_STRINGS[ExchangeId.EXCHANGEID_NSEFO];
        } else if (isBSE) {
            return EXCHANGE_STRINGS[ExchangeId.EXCHANGEID_BSEFO];
        } else {
            return null;
        }
    }
}

/** Base plan interface  */
export interface Plan {
    id: number;
    plan_name: string;
    live_trading: boolean;
    forward_testing: boolean;
    max_active_algo: number;
    max_saved_algo: number;
    backtesting: boolean;
    backtest_per_day: number;
    backtest_per_month: number;
    backtest_data: number;
    broker: string;
    algo_permissions: number[];
    internal_name: string;
    level: number;
    min_multiplier: number;
    max_multiplier: number;
    use_custom_brokerage: boolean;
    apple_plan_key: string;
    allowed_strategies: string[];
    is_broker_plan: boolean;
    is_subscribable: boolean;
    auto_deployment: boolean;
    auto_deployment_start_day: number | null;
    auto_deployment_days: number | null;
    after_market_deployment: boolean;
    ai_one_ai_credits: number;
    ai_one_ai_daily_limit: number;
    ai_one_ai_monthly_limit: number;
    forward_testing_count?: number;
    metadata: {
        count: {
            nse_count: number;
            bse_count: number;
        };
    };

    buttonLabel?: string; // FE only
    brokers?: string[]; // expected from BE
}

/** User's current plan  */
export interface UserPlan extends Plan {
    pricing_id: number;
    price: number;
    duration: number;
    frequency: string;
    is_approval_required_plan: boolean;
    is_default_plan: boolean;
    most_popular: boolean;
}

/** Multi-Broker Pricing interface */
export interface PlanPricing {
    id: number;
    duration: number;
    price: number;
    frequency: string;
    discount: number;
    discounted_price: number;
    is_default: boolean;
    brokerage_value: number;
    discounted_brokerage_value: number;
    plan_type: number;
    is_approval_required_plan: boolean;
    buttonLabel?: string; // FE only

    // Legacy Fields - Verify Usage
    // subscriber_count: number;
    // coupon_ids: string[];
    // brokerage_discount: number;
    is_subscribable?: boolean;
    is_promotion_plan?: boolean;
}

/** API Response wrapper */
export interface MbPlanResponse {
    success: boolean;
    message: string;
    data: {
        plan: UserPlan;
        pricing: PlanPricing;
        is_algo_provider: boolean;
        insufficient_balance: boolean;

        // Legacy Fields - Verify Usage
        subscription_end_date: string;
        subscription_start_date: string;
        subscription_status: string;
        subscription_payment_gateway: string;
        subscription_payment_mode: string;
        is_auto_pay_off: boolean;
    };
}

/** Current plan class */
export class CurrentPlan {
    plan: UserPlan;
    pricing: PlanPricing;
    is_algo_provider: boolean;
    insufficient_balance: boolean;
    plan_type?: number;
    price: number;
    discount: number;
    subscription_end_date: string;
    subscription_start_date: string;
    subscription_status: string;
    is_autopay_off: boolean;
    subscription_payment_gateway: string;
    subscription_payment_mode: string;

    constructor(response: MbPlanResponse) {
        const { 
            plan, 
            pricing, 
            is_algo_provider, 
            insufficient_balance, 
            subscription_end_date, 
            subscription_start_date,
            subscription_status,
            is_auto_pay_off,
            subscription_payment_gateway,
            subscription_payment_mode
        } = response.data;
        this.plan = plan;
        this.pricing = pricing;
        this.price = pricing.price;
        this.discount = pricing.discount;
        this.plan_type = pricing.plan_type;
        this.is_algo_provider = is_algo_provider;
        this.insufficient_balance = insufficient_balance;
        this.subscription_end_date = subscription_end_date;
        this.subscription_start_date = subscription_start_date;
        this.subscription_status = subscription_status;
        this.is_autopay_off = is_auto_pay_off ?? false;
        this.subscription_payment_gateway = subscription_payment_gateway;
        this.subscription_payment_mode = subscription_payment_mode;
    }
}

/** Catalog plan – plan from plans catalog with multiple pricing options */
export interface CatalogPlan extends Plan {
    pricing_options: PlanPricing[];
    most_popular?: boolean;
    is_default?: boolean;
}

export interface PlansCatalogResponse {
    data: {
        count: number;
        plans: CatalogPlan[];
    };
    success: boolean;
}

/** Chargebee hosted page from subscribe API */
export interface ChargebeeHostedPage {
    id: string;
    type: string;
    url: string;
    state: string;
    failure_reason: string;
    embed: boolean;
    created_at: number;
    expires_at: number;
    object: string;
}

/** Subscribe plan API response – checkout (hosted_page) or downgrade (subscription) */
export interface SubscribePlanResponse {
    success: boolean;
    error: string;
    data: {
        gateway?: string;
        isDowngrade?: boolean;
        result: {
            hosted_page?: ChargebeeHostedPage;
            subscription?: Record<string, unknown>;
            customer?: Record<string, unknown>;
            card?: Record<string, unknown>;
        };
    };
}

export interface PlanSubscriptionStatusResponse {
    success: boolean;
    data?: {
        id: string;
        state: SubscriptionState;
        subscription_id: string;
        customer_id: string;
        invoice_id: string;
        pass_thru_content: string;
        created_at: number;
        updated_at: number;
    }
}

export enum SubscriptionState {
    Created = 'created',
    Requested = 'requested',
    Succeeded = 'succeeded',
    Cancelled = 'cancelled',
    Failed = 'failed',
}

export enum PlanAlertType {
    PAYMENT_UNSUCCESSFUL = 'PAYMENT_UNSUCCESSFUL',
    PLAN_EXPIRED = 'PLAN_EXPIRED',
    PLAN_AUTORENEWAL = 'PLAN_AUTORENEWAL',
    PLAN_VALIDITY = 'PLAN_VALIDITY',
    PLAN_ZERO = 'PLAN_ZERO'
  }

export interface PlansRemoteConfig {
    disablePlans: boolean;
    disablePlansMessage: string;
    disablePlanAlerts: boolean;
    alertConfig: Record<PlanAlertType, { 
        alertText: string, 
        alertLink?: string
    }>;
}

/** Single subscription-state entry for credits-exhausted dialog copy (e.g. Firebase remote config). */
export interface CreditsExhaustedDialogItem {
    title: string;
    body: string;
    show_upgrade: boolean;
    show_top_up: boolean;
}

export interface CreditsExhaustedDialogConfig extends Record<PlanSubscription, CreditsExhaustedDialogItem> {}

export interface SignedRedirectUrlResponse {
    success: boolean;
    data?: {
        signed_url: string;
    };
}

export interface WalletBalanceData {
    available_credits: number;
    base_allocated: number;
    base_available: number;
    base_resets_on: string;
    base_used: number;
    earned_available: number;
    earned_credits: number;
    earned_used: number;
    topup_available: number;
    topup_credits: number;
    topup_used: number;
}

export interface WalletBalanceResponse {
    success: boolean;
    data?: WalletBalanceData;
}